import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverHangouts() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "where-to-hangout-with-friends-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park",
    "section-2": "Kitsilano Beach",
    "section-3": "Pizzeria Ludica (Board Game Café)",
    "section-4": "Catfé (Cat Café)",
    "section-5": "Guilt & Co. (Live Music Bar)",
    "section-6": "Commodore Lanes & Billiards",
    "section-7": "GRETA Bar (Arcade Bar)",
    "section-8": "Grouse Mountain",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "City of Vancouver – Stanley Park",
    },
    {
      url: "https://tripplanning.translink.ca/",
      label: "TransLink Trip Planner",
    },
    {
      url: "https://www.destinationvancouver.com/activities/attractions/",
      label: "Destination Vancouver – Attractions & Events",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location "id" matches the corresponding <Marker> ref in the article.
  // Coordinates from the conversation (approx. 5 decimals):
  const locations = [
    {
      id: "section-1",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park (Outdoor urban park with scenic vistas)",
    },
    {
      id: "section-2",
      lat: 49.27389,
      lng: -123.15511,
      popupText: "Kitsilano Beach (Popular beach & gathering spot)",
    },
    {
      id: "section-3",
      lat: 49.27998,
      lng: -123.10800,
      popupText: "Pizzeria Ludica (Board Game Café near Chinatown)",
    },
    {
      id: "section-4",
      lat: 49.27998,
      lng: -123.10714,
      popupText: "Catfé (Adoptable rescue cats in a cozy lounge)",
    },
    {
      id: "section-5",
      lat: 49.28364,
      lng: -123.10412,
      popupText: "Guilt & Co. (Live Music Bar in Gastown)",
    },
    {
      id: "section-6",
      lat: 49.28155,
      lng: -123.12100,
      popupText: "Commodore Lanes & Billiards (Bowling & Pool)",
    },
    {
      id: "section-7",
      lat: 49.28240,
      lng: -123.10612,
      popupText: "GRETA Bar (Arcade Bar in Gastown)",
    },
    {
      id: "section-8",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain (Outdoor Adventure)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Hang Out with Friends in Vancouver</title>
        <meta
          name="description"
          content="A detailed guide to the best places to hang out with friends in Vancouver, including parks, cafes, bars, and more."
        />
        <meta
          name="keywords"
          content="Vancouver, hangout spots, Stanley Park, Kits Beach, live music, board game cafes"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/where-to-hangout-with-friends-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Hang Out with Friends in Vancouver"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver's top group hangout spots."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/where-to-hangout-with-friends-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/where-to-hangout-with-friends-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Hang Out with Friends in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Check out these parks, beaches, cafes, and entertainment spots in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/where-to-hangout-with-friends-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Scenic view of Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver offers an incredible variety of hangout spots for you and your friends—from cozy cafes and lively bars to scenic parks and thrilling outdoor adventures. This article highlights some of the city’s best group-friendly destinations, catering to different interests, budgets, and times of day. Whether you want a chilled board game session, a casual beach outing, or an adrenaline-pumping adventure, Vancouver has it all.
            </p>

            {/* SECTION: Stanley Park */}
            <h2 id="section-1">Stanley Park</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park</strong>{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a massive urban park on the edge of downtown Vancouver, famed for its lush forests and stunning waterfront views. Surrounded by the ocean on three sides, it features a 9 km Seawall path and numerous trails, perfect for biking, jogging, or strolling. 
            </p>
            <ul>
              <li>
                <em>Activities:</em> Cycle the Seawall, visit the Totem Poles at Brockton Point, explore beaches, or check out the Vancouver Aquarium (ticketed).
              </li>
              <li>
                <em>Cost:</em> Free to enter; bike rentals ~$10–15/hour; parking ~$3.50/hour.
              </li>
              <li>
                <em>Hours:</em> Open 24/7; facilities follow separate daytime schedules.
              </li>
            </ul>

            {/* SECTION: Kitsilano Beach */}
            <h2 id="section-2">Kitsilano Beach</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kitsilano Beach</strong>{" "}
              <Marker
                coordY={49.27389}
                coordX={-123.15511}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , commonly called “Kits Beach,” is one of Vancouver’s most popular hangouts. It offers a laid-back vibe and gorgeous ocean views. In summer, it’s bustling with volleyball players, sunbathers, and swimmers. In cooler months, it’s still a picturesque spot to relax, walk, or grab a nearby coffee.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Beach volleyball, ocean swimming, and strolling the adjacent promenade. Kitsilano Pool (seasonal) offers heated salt-water swims.
              </li>
              <li>
                <em>Cost:</em> Free beach access; Kits Pool ~$7 entry; parking ~$3/hour.
              </li>
              <li>
                <em>Hours:</em> Beach open 24/7; lifeguards in summer, daytime hours.
              </li>
            </ul>

            {/* SECTION: Pizzeria Ludica (Board Game Café) */}
            <h2 id="section-3">Pizzeria Ludica (Board Game Café)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Pizzeria Ludica</strong>{" "}
              <Marker
                coordY={49.27998}
                coordX={-123.10800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              is a cozy place to enjoy pizza and a vast library of board games. Located near Chinatown, it’s perfect for a budget-friendly night of gaming with friends.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Play classics like Settlers of Catan, Carcassonne, or party games. The staff can suggest or teach games.
              </li>
              <li>
                <em>Cost:</em> Free to play with meal purchase; pizzas ~$14–17.
              </li>
              <li>
                <em>Hours:</em> Mon–Thu 5–10pm, Fri–Sat 4–11pm, Sun 4–10pm.
              </li>
            </ul>

            {/* SECTION: Catfé (Cat Café) */}
            <h2 id="section-4">Catfé (Cat Café)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Catfé</strong>{" "}
              <Marker
                coordY={49.27998}
                coordX={-123.10714}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              is Vancouver’s first cat café, where you can sip coffee and hang out with adorable, adoptable rescue cats. Reservations are recommended, as capacity is limited.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Interact with adoptable cats in a special lounge. Take photos, play with toys, enjoy a latte.
              </li>
              <li>
                <em>Cost:</em> ~$16.75 for a 55-minute cat lounge session; drinks ~$4–7.
              </li>
              <li>
                <em>Hours:</em> Generally 11am–7pm most days (check website); booking required.
              </li>
            </ul>

            {/* SECTION: Guilt & Co. (Live Music Bar) */}
            <h2 id="section-5">Guilt & Co. (Live Music Bar in Gastown)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Guilt & Company</strong>{" "}
              <Marker
                coordY={49.28364}
                coordX={-123.10412}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              is a trendy underground bar in Gastown offering nightly live music and a cozy speakeasy feel. Expect a variety of genres, from jazz to funk.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Live music, cocktails, and sometimes board games on tables.
              </li>
              <li>
                <em>Cost:</em> No cover; pay-what-you-can to the band. Drinks ~$8–15.
              </li>
              <li>
                <em>Hours:</em> Typically opens 6pm; closes midnight–2am depending on the night.
              </li>
            </ul>

            {/* SECTION: Commodore Lanes & Billiards */}
            <h2 id="section-6">Commodore Lanes & Billiards</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Commodore Lanes & Billiards</strong>{" "}
              <Marker
                coordY={49.28155}
                coordX={-123.12100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              is a vintage bowling alley right in downtown Vancouver. With 5-pin bowling, pool tables, and arcade games, it’s a classic choice for a group night out.
            </p>
            <ul>
              <li>
                <em>Activities:</em> 5-pin bowling, billiards, foosball, arcade games.
              </li>
              <li>
                <em>Cost:</em> Lane rentals ~$59/hour (up to 6 ppl), billiards ~$19/hour/table.
              </li>
              <li>
                <em>Hours:</em> Sun–Thu ~11am–12am, Fri & Sat may open earlier or close later.
              </li>
            </ul>

            {/* SECTION: GRETA Bar (Arcade Bar) */}
            <h2 id="section-7">GRETA Bar (Arcade Bar in Gastown)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>GRETA</strong>{" "}
              <Marker
                coordY={49.28240}
                coordX={-123.10612}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              is a lively arcade bar featuring old-school video games, skee-ball, basketball hoops, and more. It’s the perfect mix of a nightclub vibe and gaming fun.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Classic arcade machines (Mario Kart, pinball), socializing, dancing.
              </li>
              <li>
                <em>Cost:</em> No cover on most nights; pay per game (~$1/game). Drinks ~$7–12.
              </li>
              <li>
                <em>Hours:</em> Opens ~4pm; closes 2–3am on weekends.
              </li>
            </ul>

            {/* SECTION: Grouse Mountain */}
            <h2 id="section-8">Grouse Mountain</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Grouse Mountain</strong>{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.08180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              overlooks Vancouver from the North Shore and offers year-round activities—from hiking in summer to skiing in winter. Ride the gondola for breathtaking views or challenge yourself on the Grouse Grind.
            </p>
            <ul>
              <li>
                <em>Activities:</em> Hiking the Grouse Grind, skiing/boarding, ziplining, wildlife refuge tours.
              </li>
              <li>
                <em>Cost:</em> Skyride ~$60 round-trip, or $20 down-only if you hike up. Ski day passes ~$70.
              </li>
              <li>
                <em>Hours:</em> Daily, roughly 8am–10pm (varies by season).
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do we need reservations or tickets in advance?</h3>
              <p>
                Many outdoor spots like Stanley Park or Kits Beach are free and don’t need reservations. Venues like Pizzeria Ludica, Catfé, and some bowling lanes often benefit from bookings. For Grouse Mountain, you can buy tickets on-site or online in advance.
              </p>

              <h3>2. Can minors join at these places?</h3>
              <p>
                Stanley Park, Kits Beach, Catfé, Ludica, Commodore Lanes are all-ages friendly (with some late-night restrictions). Bars like Guilt & Co. and GRETA are 19+ only due to alcohol licensing.
              </p>

              <h3>3. What if it rains?</h3>
              <p>
                Vancouver often has wet weather, but plenty of indoor fun awaits—try the bowling alley, board game cafes, or Catfé. You can also visit museums or indoor attractions if you need to escape the rain.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Drinking alcohol in public spaces like parks and beaches is generally not permitted unless in designated areas (pilot projects may apply). Obey all posted signs regarding BBQs, noise limits, and park closing times. At bars and licensed venues, the legal drinking age in British Columbia is 19—IDs will be checked. Always check official resources for changes or updates, and plan responsibly if you’re driving.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you crave scenic outdoor adventures or cozy indoor gatherings, Vancouver’s diverse hangout spots have you covered. From free beach days and park picnics to bustling arcades and live music nights, there’s something for every group. Plan ahead, consider your group’s vibe, and explore the city’s vibrant social scene. Have fun, stay safe, and enjoy all that Vancouver has to offer!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}